import React, { useMemo, useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Box,
    Grid,
    TextField,
    Button,
    Autocomplete,
    Collapse,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    AutoAwesome as AutoAwesomeIcon,
    CheckCircle as CheckCircleIcon,
    CloudUpload as CloudUploadIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';
import DetailedItineraryModal from '../../../components/ItineraryTabs/DetailedItineraryModal';
import { findMatchingItineraryCard, isTitleInDb } from '../createInquiryCalculation';
import { createItineraryCard, updateItineraryCard } from '../../../api/itineraryCardsAPI';

const ItineraryCard = ({
    itinerary,
    onItineraryChange,
    allPackages,
    onAddDay,
    onRemoveDay,
    onCopyText,
    onDetailedItineraryChange,
    dayWiseDetailedItinerary = [],
    storedItineraries = [],
    isLocationValid = true,
    destination = '',
    onCardCreated,
    onCardUpdated,
    showSnackbar,
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedDayIndex, setSelectedDayIndex] = useState(null);
    const [detailedItinerary, setDetailedItinerary] = useState(dayWiseDetailedItinerary || []);
    const [savingDayIdx, setSavingDayIdx] = useState(null);

    // Sync detailedItinerary when dayWiseDetailedItinerary prop changes
    useEffect(() => {
        if (dayWiseDetailedItinerary && dayWiseDetailedItinerary.length > 0) {
            setDetailedItinerary(dayWiseDetailedItinerary);
        }
    }, [dayWiseDetailedItinerary]);

    const safeItinerary = itinerary || [];

    // Auto-match short itinerary day with stored itinerary cards whenever itinerary or storedItineraries change
    useEffect(() => {
        if (!storedItineraries || storedItineraries.length === 0 || !itinerary || itinerary.length === 0) return;
        let needsUpdate = false;
        const updated = [...detailedItinerary];

        itinerary.forEach((dayTitle, idx) => {
            if (dayTitle && typeof dayTitle === 'string' && dayTitle.trim()) {
                const current = updated[idx];
                const hasExisting = Boolean(current?.para?.length && current.para.some(p => p && p.trim()));
                if (!hasExisting) {
                    const matched = findMatchingItineraryCard(dayTitle, storedItineraries, allPackages);
                    if (matched && matched.description) {
                        updated[idx] = {
                            dayTitle,
                            para: [matched.description],
                            isAutoPopulated: true,
                            matchedCardTitle: matched.title
                        };
                        needsUpdate = true;
                    }
                }
            }
        });

        if (needsUpdate) {
            setDetailedItinerary(updated);
            if (onDetailedItineraryChange) {
                onDetailedItineraryChange(updated);
            }
        }
    }, [itinerary, storedItineraries, allPackages]);

    // Handle day title selection or typing in short itinerary Autocomplete
    const handleDayTitleChange = (index, newTitle) => {
        onItineraryChange(index, newTitle || '');

        if (newTitle && newTitle.trim()) {
            const matched = findMatchingItineraryCard(newTitle, storedItineraries, allPackages);
            if (matched && matched.description) {
                const updated = [...detailedItinerary];
                const current = updated[index];
                // Auto-fill full itinerary if not manually custom-edited by user
                if (!current?.isManualEdited || !current?.para?.length) {
                    updated[index] = {
                        dayTitle: newTitle,
                        para: [matched.description],
                        isAutoPopulated: true,
                        matchedCardTitle: matched.title
                    };
                    setDetailedItinerary(updated);
                    if (onDetailedItineraryChange) {
                        onDetailedItineraryChange(updated);
                    }
                }
            }
        }
    };

    const handleSaveDetailedItinerary = (index, detailedData) => {
        const updated = [...detailedItinerary];
        const newDayTitle = detailedData?.dayTitle || safeItinerary[index] || `Day ${index + 1}`;
        updated[index] = {
            ...detailedData,
            dayTitle: newDayTitle,
            isManualEdited: detailedData?.isManualEdited !== undefined ? detailedData.isManualEdited : true,
            isAutoPopulated: Boolean(detailedData?.isAutoPopulated),
        };
        setDetailedItinerary(updated);
        if (onDetailedItineraryChange) {
            onDetailedItineraryChange(updated);
        }
        if (detailedData?.dayTitle && detailedData.dayTitle !== safeItinerary[index]) {
            onItineraryChange(index, detailedData.dayTitle);
        }
        setModalOpen(false);
        setSelectedDayIndex(null);
    };

    // One-click save to Database directly from the day row
    const handleSaveDayRowToDb = async (index) => {
        const title = (safeItinerary[index] || '').trim();
        if (!title) return;
        const dayDetail = detailedItinerary[index];
        const matched = (!dayDetail?.para?.length && title) ? findMatchingItineraryCard(title, storedItineraries, allPackages) : null;
        const paras = (dayDetail?.para && dayDetail.para.length > 0)
            ? dayDetail.para
            : (matched?.description ? [matched.description] : []);

        const desc = paras.filter(Boolean).join('<p><br></p>');
        if (!desc) {
            setSelectedDayIndex(index);
            setModalOpen(true);
            return;
        }

        setSavingDayIdx(index);
        try {
            const destToSave = (destination || 'Sikkim').trim();
            const res = await createItineraryCard({
                title,
                destination: destToSave,
                description: desc,
            });

            if (res && (res.success || res._id || res.data)) {
                const savedCard = res.data || res;
                if (onCardCreated) onCardCreated(savedCard);
                if (showSnackbar) showSnackbar(`Saved "${title}" to Database Itinerary Library!`, 'success');

                const updated = [...detailedItinerary];
                updated[index] = {
                    ...(updated[index] || {}),
                    dayTitle: title,
                    para: paras,
                    isAutoPopulated: true,
                    isManualEdited: false,
                    matchedCardTitle: title
                };
                setDetailedItinerary(updated);
                if (onDetailedItineraryChange) {
                    onDetailedItineraryChange(updated);
                }
            } else {
                if (showSnackbar) showSnackbar(res?.message || 'Failed to save to database', 'error');
            }
        } catch (err) {
            console.error('Failed to save day to DB:', err);
            if (showSnackbar) {
                showSnackbar(err.response?.data?.message || err.message || 'Failed to save to database', 'error');
            }
        } finally {
            setSavingDayIdx(null);
        }
    };

    // One-click UPDATE to Database directly from the day row
    const handleUpdateDayRowInDb = async (index) => {
        const title = (safeItinerary[index] || '').trim();
        if (!title) return;
        const dayDetail = detailedItinerary[index];
        const desc = (dayDetail?.para || []).filter(Boolean).join('<p><br></p>');
        if (!desc) return;

        // Find existing card by ID, title, or matchedCardTitle in storedItineraries
        const existing = (storedItineraries || []).find(c => {
            return (dayDetail?.cardId && c?._id === dayDetail.cardId) ||
                   cleanStr(c?.title) === cleanStr(title) ||
                   (dayDetail?.matchedCardTitle && cleanStr(c?.title) === cleanStr(dayDetail.matchedCardTitle));
        });
        if (!existing?._id) return;

        setSavingDayIdx(index);
        try {
            const destToSave = (destination || existing.destination || 'Sikkim').trim();
            const res = await updateItineraryCard(existing._id, {
                title,
                destination: destToSave,
                description: desc,
            });

            if (res && (res.success || res.data || res._id)) {
                const updatedCard = res.data || res;
                if (onCardUpdated) onCardUpdated(updatedCard);
                if (showSnackbar) showSnackbar(`Updated "${title}" in Database Library!`, 'success');

                const updated = [...detailedItinerary];
                updated[index] = {
                    ...(updated[index] || {}),
                    dayTitle: title,
                    para: dayDetail.para,
                    isAutoPopulated: true,
                    isManualEdited: false,
                    matchedCardTitle: title,
                    cardId: updatedCard._id
                };
                setDetailedItinerary(updated);
                if (onDetailedItineraryChange) {
                    onDetailedItineraryChange(updated);
                }
            } else {
                if (showSnackbar) showSnackbar(res?.message || 'Failed to update database', 'error');
            }
        } catch (err) {
            console.error('Failed to update day in DB:', err);
            if (showSnackbar) {
                showSnackbar(err.response?.data?.message || err.message || 'Failed to update database', 'error');
            }
        } finally {
            setSavingDayIdx(null);
        }
    };

    // Extract unique itinerary descriptions for autocomplete, prioritized from stored itinerary cards
    const uniqueItineraryOptions = useMemo(() => {
        const options = new Set();
        // 1. Add all stored itinerary card titles from the database (all seeded items)
        if (storedItineraries && Array.isArray(storedItineraries)) {
            storedItineraries.forEach(card => {
                if (card?.title && card.title.trim()) {
                    options.add(card.title.trim());
                }
            });
        }
        // 2. Add package short itineraries
        if (allPackages && Array.isArray(allPackages)) {
            allPackages.forEach(pkg => {
                if (pkg.details?.shortItinerary && Array.isArray(pkg.details.shortItinerary)) {
                    pkg.details.shortItinerary.forEach(item => {
                        const text = typeof item === 'string' ? item : (item.tagValue || item.tagName || '');
                        if (text && text.trim().length > 0) {
                            options.add(text.trim());
                        }
                    });
                }
            });
        }
        return Array.from(options);
    }, [storedItineraries, allPackages]);

    // Helper to check DB match
    const cleanStr = (s) =>
        String(s || '')
            .toLowerCase()
            .replace(/^(?:day\s*\d+\s*[:\-–]?\s*|\d+[\.\)]\s*)/i, '')
            .replace(/[^a-z0-9]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

    if (safeItinerary.length === 0 && !onAddDay) return null;

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: isExpanded ? 2 : 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        cursor: 'pointer',
                        flex: 1,
                        transition: 'opacity 0.2s',
                        '&:hover': { opacity: 0.8 }
                    }}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Short Itinerary
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        {safeItinerary.length > 0 && (
                            <Chip label={`${safeItinerary.length} Day${safeItinerary.length > 1 ? 's' : ''}`} size="small" color="primary" variant="filled" />
                        )}
                    </Stack>
                </Box>
                {onAddDay && (
                    <Tooltip title={!isLocationValid ? "Please select a location in Trip Details first" : ""}>
                        <span>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={onAddDay}
                                disabled={!isLocationValid}
                            >
                                + Add Day
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>
            <Collapse in={isExpanded}>
                {!isLocationValid && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5, fontWeight: 500 }}>
                        ⚠️ Location is required. Please select an approved Location under <strong>Trip Details</strong> above before adding or editing itinerary days.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {safeItinerary.map((day, index) => {
                        const dayHasTitle = Boolean(day && day.trim());
                        const dayDetail = detailedItinerary[index];
                        const hasDetails = Boolean(dayDetail?.para?.length && dayDetail.para.some(p => p && p.trim()));
                        const matchedFallback = (!hasDetails && dayHasTitle) ? findMatchingItineraryCard(day, storedItineraries, allPackages) : null;
                        const effectiveHasDetails = hasDetails || Boolean(matchedFallback && matchedFallback.description);
                        const isAuto = Boolean(dayDetail?.isAutoPopulated || (matchedFallback && !dayDetail?.isManualEdited));
                        const isManual = Boolean(dayDetail?.isManualEdited);

                        // Check if day is already saved in DB library
                        const isStoredInDb = Boolean(
                            dayHasTitle && (
                                isTitleInDb(day, storedItineraries) ||
                                (dayDetail?.cardId && (storedItineraries || []).some(c => c._id === dayDetail.cardId)) ||
                                (dayDetail?.matchedCardTitle && isTitleInDb(dayDetail.matchedCardTitle, storedItineraries))
                            )
                        );

                        return (
                            <Grid container key={index} alignItems="flex-start" spacing={2}>
                                <Grid item xs={12} md={2} sx={{ pt: '24px !important' }}>
                                    <Typography variant="subtitle1" fontWeight={600} sx={{ color: !isLocationValid ? '#94a3b8' : 'inherit' }}>
                                        Day {index + 1} :
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={5}>
                                    <Autocomplete
                                        freeSolo
                                        size="small"
                                        disabled={!isLocationValid}
                                        openOnFocus={true}
                                        options={uniqueItineraryOptions}
                                        value={String(day || '')}
                                        onInputChange={(event, newInputValue) => {
                                            handleDayTitleChange(index, newInputValue || '');
                                        }}
                                        onChange={(event, newValue) => {
                                            handleDayTitleChange(index, newValue || '');
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                multiline
                                                minRows={1}
                                                disabled={!isLocationValid}
                                                placeholder={!isLocationValid ? "Select location above first..." : `Select or enter day ${index + 1} itinerary`}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={5} sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: '24px !important', flexWrap: 'wrap' }}>
                                    <Tooltip
                                        title={
                                            !isLocationValid
                                                ? "Select location first"
                                                : effectiveHasDetails
                                                ? isManual
                                                    ? "Custom details saved. Click to view or edit."
                                                    : "Full itinerary auto-loaded from DB! Click to view or edit."
                                                : "No full itinerary details yet. Click to add."
                                        }
                                    >
                                        <span>
                                            <Button
                                                size="small"
                                                disabled={!isLocationValid}
                                                variant={effectiveHasDetails ? "contained" : "outlined"}
                                                color={effectiveHasDetails ? (isManual ? "success" : "primary") : "inherit"}
                                                startIcon={
                                                    effectiveHasDetails ? (
                                                        isManual ? <CheckCircleIcon fontSize="small" /> : <AutoAwesomeIcon fontSize="small" />
                                                    ) : (
                                                        <EditIcon fontSize="small" />
                                                    )
                                                }
                                                onClick={() => {
                                                    setSelectedDayIndex(index);
                                                    if (!hasDetails && matchedFallback && matchedFallback.description) {
                                                        const updated = [...detailedItinerary];
                                                        updated[index] = {
                                                            dayTitle: day,
                                                            para: [matchedFallback.description],
                                                            isAutoPopulated: true,
                                                            matchedCardTitle: matchedFallback.title
                                                        };
                                                        setDetailedItinerary(updated);
                                                        if (onDetailedItineraryChange) {
                                                            onDetailedItineraryChange(updated);
                                                        }
                                                    }
                                                    setModalOpen(true);
                                                }}
                                                sx={{
                                                    minWidth: "auto",
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    borderRadius: 1.5,
                                                    px: 1.5,
                                                    fontSize: 12,
                                                    ...(effectiveHasDetails && !isManual ? {
                                                        bgcolor: '#0284c7',
                                                        color: '#fff',
                                                        '&:hover': { bgcolor: '#0369a1' }
                                                    } : effectiveHasDetails && isManual ? {
                                                        bgcolor: '#16a34a',
                                                        color: '#fff',
                                                        '&:hover': { bgcolor: '#15803d' }
                                                    } : {})
                                                }}
                                            >
                                                {dayHasTitle && effectiveHasDetails ? (isManual ? "Edited Details" : "Full Itinerary ✨") : "+ Details"}
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    {/* Direct Save to DB button for new/custom itineraries on the row */}
                                    {dayHasTitle && effectiveHasDetails && !isStoredInDb && isLocationValid && (
                                        <Tooltip title="Save this new itinerary to Database Library so it auto-populates in future inquiries">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                                disabled={savingDayIdx === index}
                                                startIcon={
                                                    savingDayIdx === index ? (
                                                        <CircularProgress size={14} color="inherit" />
                                                    ) : (
                                                        <CloudUploadIcon fontSize="small" />
                                                    )
                                                }
                                                onClick={() => handleSaveDayRowToDb(index)}
                                                sx={{
                                                    minWidth: "auto",
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    borderRadius: 1.5,
                                                    px: 1.2,
                                                    fontSize: 12,
                                                    borderColor: '#a855f7',
                                                    color: '#9333ea',
                                                    '&:hover': {
                                                        borderColor: '#7c3aed',
                                                        bgcolor: '#faf5ff'
                                                    }
                                                }}
                                            >
                                                {savingDayIdx === index ? "Saving..." : "Save to DB 💾"}
                                            </Button>
                                        </Tooltip>
                                    )}

                                    {/* Direct Update DB button when day is stored in DB but user has edited details */}
                                    {dayHasTitle && effectiveHasDetails && isStoredInDb && isManual && isLocationValid && (
                                        <Tooltip title="Update the Database Library card with your latest edits">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                disabled={savingDayIdx === index}
                                                startIcon={
                                                    savingDayIdx === index ? (
                                                        <CircularProgress size={14} color="inherit" />
                                                    ) : (
                                                        <SyncIcon fontSize="small" />
                                                    )
                                                }
                                                onClick={() => handleUpdateDayRowInDb(index)}
                                                sx={{
                                                    minWidth: "auto",
                                                    fontWeight: 600,
                                                    textTransform: "none",
                                                    borderRadius: 1.5,
                                                    px: 1.2,
                                                    fontSize: 12,
                                                    borderColor: '#f59e0b',
                                                    color: '#d97706',
                                                    '&:hover': {
                                                        borderColor: '#d97706',
                                                        bgcolor: '#fffbeb'
                                                    }
                                                }}
                                            >
                                                {savingDayIdx === index ? "Updating..." : "Update DB 🔄"}
                                            </Button>
                                        </Tooltip>
                                    )}

                                    {onCopyText && (
                                        <Button
                                            color="primary"
                                            size="small"
                                            disabled={!isLocationValid}
                                            onClick={() => onCopyText(String(day || ''))}
                                            sx={{ minWidth: 0, textTransform: 'none' }}
                                        >
                                            Copy
                                        </Button>
                                    )}
                                    {onRemoveDay && (
                                        <IconButton
                                            color="error"
                                            size="small"
                                            disabled={!isLocationValid}
                                            onClick={() => onRemoveDay(index)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Grid>
                            </Grid>
                        );
                    })}
                </Box>
            </Collapse>
            <DetailedItineraryModal
                open={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedDayIndex(null);
                }}
                dayIndex={selectedDayIndex}
                dayTitle={selectedDayIndex !== null ? (safeItinerary[selectedDayIndex] || detailedItinerary[selectedDayIndex]?.dayTitle || '') : ''}
                currentDetails={
                    selectedDayIndex !== null
                        ? (detailedItinerary[selectedDayIndex] || (
                            safeItinerary[selectedDayIndex]
                                ? (() => {
                                    const m = findMatchingItineraryCard(safeItinerary[selectedDayIndex], storedItineraries, allPackages);
                                    return m ? { dayTitle: safeItinerary[selectedDayIndex], para: [m.description], isAutoPopulated: true, cardId: m._id, matchedCardTitle: m.title } : null;
                                })()
                                : null
                        ))
                        : null
                }
                onSave={handleSaveDetailedItinerary}
                onTitleChange={(idx, newTitle) => {
                    onItineraryChange(idx, newTitle);
                }}
                selectedDestination={destination || ''}
                storedItineraries={storedItineraries}
                onCardCreated={(newCard) => {
                    if (onCardCreated) onCardCreated(newCard);
                }}
                onCardUpdated={(updatedCard) => {
                    if (onCardUpdated) onCardUpdated(updatedCard);
                }}
                showSnackbar={showSnackbar}
            />
        </Paper>
    );
};

export default ItineraryCard;
